// ── Agenda Tab ────────────────────────────────────────────────────────────────
import { getTrip }                        from '@/store/tripState';
import { saveTrip }                       from '@/services/sync.service';
import { fmt, toArr, fmtR, sanitize }    from '@/helpers';
import { getPayerName }                   from '@/tabs/viagem';
import { attachmentViewerUrl }            from '@/ui/attachmentViewer';
import { AGENDA_CAT_ICONS,
         AGENDA_STATUS_CONFIG }           from '@/constants';
import type { AgendaItem, AgendaViewMode, AgendaStatus } from '@/types';

// ── View mode state ───────────────────────────────────────────
let _viewMode: AgendaViewMode = 'timeline';

// ── Accordion state (expanded days) ──────────────────────────
const _expandedDays = new Set<string>();
let _agendaFirstRender = true;

// ── Filter state ──────────────────────────────────────────────
function getFilters() {
  return {
    day:    (document.getElementById('agenda-filter-day')    as HTMLInputElement)?.value  ?? '',
    cat:    (document.getElementById('agenda-filter-cat')    as HTMLSelectElement)?.value ?? '',
    status: (document.getElementById('agenda-filter-status') as HTMLSelectElement)?.value ?? '',
    search: ((document.getElementById('agenda-filter-search') as HTMLInputElement)?.value ?? '').toLowerCase(),
  };
}

export function toggleAgendaDay(date: string): void {
  if (_expandedDays.has(date)) _expandedDays.delete(date);
  else _expandedDays.add(date);
  renderAgenda();
}

export function toggleAgendaFilters(): void {
  const panel = document.getElementById('agenda-filters-panel');
  const btn   = document.getElementById('agenda-filter-toggle');
  if (!panel) return;
  const open = panel.classList.toggle('open');
  if (btn) btn.setAttribute('aria-expanded', String(open));
}

export function setAgendaView(mode: AgendaViewMode): void {
  _viewMode = mode;
  // Update button active states
  ['timeline','cards','compact'].forEach(m => {
    document.getElementById(`agenda-view-${m}`)?.classList.toggle('active', m === mode);
  });
  renderAgenda();
}

export function filterAgenda(): void {
  renderAgenda();
}

export function clearAgendaFilters(): void {
  ['agenda-filter-day','agenda-filter-cat','agenda-filter-status','agenda-filter-search']
    .forEach(id => {
      const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
      if (el) el.value = '';
    });
  _agendaFirstRender = true;
  renderAgenda();
}

// ── Main render ───────────────────────────────────────────────
export function renderAgenda(): void {
  const el = document.getElementById('agenda-container');
  if (!el) return;

  const { day, cat, status, search } = getFilters();
  let arr = toArr(getTrip().agenda ?? {}) as AgendaItem[];

  // Apply filters
  if (day)    arr = arr.filter(a => a.date === day);
  if (cat)    arr = arr.filter(a => a.cat === cat);
  if (status) arr = arr.filter(a =>
    status === 'feito' ? a.done || a.status === 'feito'
    : a.status === status || (!a.status && status === 'pendente')
  );
  if (search) arr = arr.filter(a =>
    (a.title ?? '').toLowerCase().includes(search) ||
    (a.local ?? '').toLowerCase().includes(search) ||
    (a.notes ?? '').toLowerCase().includes(search)
  );

  // Sort by date then time
  arr.sort((a, b) =>
    (a.date ?? '').localeCompare(b.date ?? '') ||
    (a.time ?? '').localeCompare(b.time ?? '')
  );

  const pending  = arr.filter(a => !a.done && a.status !== 'feito');
  const finished = arr.filter(a => a.done  || a.status === 'feito');

  if (!arr.length) {
    el.innerHTML = '<div class="empty">Nenhum item encontrado.</div>';
    return;
  }

  switch (_viewMode) {
    case 'timeline': el.innerHTML = renderTimeline(pending, finished); break;
    case 'cards':    el.innerHTML = renderCards(pending, finished);    break;
    case 'compact':  el.innerHTML = renderCompact(pending, finished);  break;
  }
}

// ── Timeline view — accordion por dia ────────────────────────
function renderTimeline(pending: AgendaItem[], finished: AgendaItem[]): string {
  const today  = new Date().toISOString().slice(0, 10);
  const byDate: Record<string, AgendaItem[]> = {};
  pending.forEach(a => { (byDate[a.date ?? 'sem data'] ??= []).push(a); });

  const dates = Object.keys(byDate).sort();

  // On first render, auto-expand today or nearest upcoming date
  if (_agendaFirstRender && dates.length) {
    _agendaFirstRender = false;
    const upcoming = dates.find(d => d >= today) ?? dates[dates.length - 1];
    _expandedDays.add(upcoming);
  }

  const dayHtml = dates.map(date => {
    const items      = byDate[date];
    const isExpanded = _expandedDays.has(date);
    const isToday    = date === today;
    return `
      <div class="agenda-day-block">
        <button class="agenda-day-toggle${isToday ? ' today' : ''}" onclick="window.__toggleAgendaDay('${date}')">
          <div style="display:flex;align-items:center;gap:8px">
            ${isToday ? '<span class="badge b-info" style="font-size:10px">Hoje</span>' : ''}
            <span class="agenda-day-date">${fmt(date)}</span>
          </div>
          <div style="display:flex;gap:6px;align-items:center">
            <span class="badge b-info" style="font-size:10px">${items.length} evento${items.length !== 1 ? 's' : ''}</span>
            <span class="agenda-day-chevron${isExpanded ? ' open' : ''}">▾</span>
          </div>
        </button>
        ${isExpanded ? `<div class="agenda-timeline-items">${items.map(a => timelineItem(a)).join('')}</div>` : ''}
      </div>`;
  }).join('');

  const doneHtml = finished.length ? `
    <div class="agenda-done-section">
      <div class="agenda-done-label">✓ Concluídos (${finished.length})</div>
      ${finished.map(a => timelineItem(a, true)).join('')}
    </div>` : '';

  return dayHtml + doneHtml;
}

function timelineItem(a: AgendaItem, isDone = false): string {
  const icon    = AGENDA_CAT_ICONS[a.cat ?? ''] ?? '📌';
  const payer   = a.pagador ? getPayerName(a.pagador) : '';
  const sConfig = AGENDA_STATUS_CONFIG[a.status ?? (isDone ? 'feito' : 'pendente')];
  const attList = Object.values(a.attachments ?? {});

  return `
    <div class="agenda-tl-item ${isDone ? 'done' : ''}">
      <div class="agenda-tl-dot" style="background:${isDone ? 'var(--muted)' : 'var(--lux-blue)'}">
        <span style="font-size:11px">${icon}</span>
      </div>
      <div class="agenda-tl-content">
        <div class="agenda-tl-header">
          <div class="agenda-tl-title ${isDone ? 'strikethrough' : ''}">${sanitize(a.title ?? '')}</div>
          <div style="display:flex;gap:4px;align-items:center;flex-shrink:0">
            ${a.time ? `<span class="agenda-tl-time">${a.time}</span>` : ''}
            <span class="badge ${sConfig?.cls ?? 'b-warn'}" style="font-size:10px">${sConfig?.label ?? 'Pendente'}</span>
          </div>
        </div>
        ${a.local ? `<div class="agenda-tl-meta">📍 ${sanitize(a.local)}</div>` : ''}
        ${a.notes ? `<div class="agenda-tl-meta">${sanitize(a.notes)}</div>` : ''}
        <div class="agenda-tl-footer">
          ${a.preco ? `<span class="agenda-tl-price">${fmtR(a.preco)}${payer ? ` · ${sanitize(payer)}` : ''}</span>` : ''}
          ${attList.map(att => `<a class="attach-chip" href="${attachmentViewerUrl(att)}" target="_blank" rel="noopener noreferrer">📎 ${sanitize(att.name)}</a>`).join('')}
        </div>
        <div class="agenda-tl-actions">
          <button class="agenda-status-btn ${isDone ? 'done' : 'pending'}"
            onclick="window.__toggleAgenda('${a.id}')">
            ${isDone ? '✓ Concluído' : '○ Marcar feito'}
          </button>
          <button class="icon-btn" onclick="window.__openAttachModal('agenda','${a.id}')">📎</button>
          <button class="icon-btn" onclick="window.__openModal('agenda','${a.id}')">✏</button>
          <button class="icon-btn del" onclick="window.__delItem('agenda','${a.id}')">×</button>
        </div>
      </div>
    </div>`;
}

// ── Cards view — richer visual ────────────────────────────────
function renderCards(pending: AgendaItem[], finished: AgendaItem[]): string {
  const renderGroup = (items: AgendaItem[], done: boolean) =>
    `<div class="agenda-cards-grid">
      ${items.map(a => {
        const icon    = AGENDA_CAT_ICONS[a.cat ?? ''] ?? '📌';
        const payer   = a.pagador ? getPayerName(a.pagador) : '';
        const sConfig = AGENDA_STATUS_CONFIG[a.status ?? (done ? 'feito' : 'pendente')];
        return `
          <div class="agenda-card ${done ? 'done' : ''}">
            <div class="agenda-card-header">
              <div class="agenda-card-icon">${icon}</div>
              <div style="flex:1;min-width:0">
                <div class="agenda-card-title ${done ? 'strikethrough' : ''}">${sanitize(a.title ?? '')}</div>
                ${a.cat ? `<div class="agenda-card-cat">${a.cat}</div>` : ''}
              </div>
              <span class="badge ${sConfig?.cls ?? 'b-warn'}" style="font-size:10px;flex-shrink:0">${sConfig?.label ?? 'Pendente'}</span>
            </div>
            <div class="agenda-card-body">
              ${a.date ? `<div class="agenda-card-row">📅 ${fmt(a.date)}${a.time ? ' às ' + a.time : ''}</div>` : ''}
              ${a.local ? `<div class="agenda-card-row">📍 ${sanitize(a.local)}</div>` : ''}
              ${a.notes ? `<div class="agenda-card-row agenda-card-notes">${sanitize(a.notes)}</div>` : ''}
              ${a.preco ? `<div class="agenda-card-row">💰 ${fmtR(a.preco)}${payer ? ' · pago por ' + sanitize(payer) : ''}</div>` : ''}
            </div>
            <div class="agenda-card-footer">
              <input type="checkbox" ${done ? 'checked' : ''} onchange="window.__toggleAgenda('${a.id}')"
                style="accent-color:var(--lux-blue);cursor:pointer">
              <div style="display:flex;gap:4px">
                <button class="icon-btn" onclick="window.__openAttachModal('agenda','${a.id}')">📎</button>
                <button class="icon-btn" onclick="window.__openModal('agenda','${a.id}')">✏</button>
                <button class="icon-btn del" onclick="window.__delItem('agenda','${a.id}')">×</button>
              </div>
            </div>
          </div>`;
      }).join('')}
    </div>`;

  const byDate: Record<string, AgendaItem[]> = {};
  pending.forEach(a => { (byDate[a.date ?? 'sem data'] ??= []).push(a); });

  return Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, items]) => `
      <div style="margin-bottom:16px">
        <div class="agenda-day-label" style="margin-bottom:10px">
          <span class="agenda-day-date">${fmt(date)}</span>
          <span class="badge b-info" style="font-size:10px">${items.length}</span>
        </div>
        ${renderGroup(items, false)}
      </div>`).join('') +
    (finished.length ? `
      <div class="agenda-done-section">
        <div class="agenda-done-label">✓ Concluídos (${finished.length})</div>
        ${renderGroup(finished, true)}
      </div>` : '');
}

// ── Compact view — dense list ─────────────────────────────────
function renderCompact(pending: AgendaItem[], finished: AgendaItem[]): string {
  const row = (a: AgendaItem, done: boolean) => {
    const icon    = AGENDA_CAT_ICONS[a.cat ?? ''] ?? '📌';
    const sConfig = AGENDA_STATUS_CONFIG[a.status ?? (done ? 'feito' : 'pendente')];
    return `
      <div class="agenda-compact-row ${done ? 'done' : ''}">
        <button class="agenda-status-btn-sm ${done ? 'done' : 'pending'}"
          onclick="window.__toggleAgenda('${a.id}')" style="flex-shrink:0">
          ${done ? '✓' : '○'}
        </button>
        <span style="font-size:16px;flex-shrink:0">${icon}</span>
        <div style="flex:1;min-width:0">
          <span class="agenda-compact-title ${done ? 'strikethrough' : ''}">${sanitize(a.title ?? '')}</span>
          ${a.time || a.local ? `<span class="agenda-compact-meta">${[a.time, a.local ? sanitize(a.local) : ''].filter(Boolean).join(' · ')}</span>` : ''}
        </div>
        <div style="display:flex;align-items:center;gap:6px;flex-shrink:0">
          ${a.date ? `<span class="agenda-compact-date">${fmt(a.date)}</span>` : ''}
          <span class="badge ${sConfig?.cls ?? 'b-warn'}" style="font-size:9px">${sConfig?.label ?? 'Pendente'}</span>
          <button class="icon-btn" onclick="window.__openModal('agenda','${a.id}')">✏</button>
          <button class="icon-btn del" onclick="window.__delItem('agenda','${a.id}')">×</button>
        </div>
      </div>`;
  };

  return [
    ...pending.map(a => row(a, false)),
    finished.length ? `<div class="agenda-done-label" style="margin-top:12px">✓ Concluídos (${finished.length})</div>` : '',
    ...finished.map(a => row(a, true)),
  ].join('');
}

export function toggleAgenda(id: string): void {
  const a = getTrip().agenda?.[id];
  if (!a) return;
  const newDone   = !a.done;
  const newStatus: AgendaStatus = newDone ? 'feito' : 'pendente';
  saveTrip(`agenda/${id}/done`,   newDone).catch(() => void 0);
  saveTrip(`agenda/${id}/status`, newStatus).catch(() => void 0);
}
